(() => {
  'use strict';

  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart.js`;
  const addUrl = `${root}cart/add.js`;
  const changeUrl = `${root}cart/change.js`;

  // Ajoute une ligne cadeau distincte pour chaque lot de deux articles payants.
  // La ligne n'est conservée que si Shopify confirme qu'elle est réellement
  // gratuite après application de la réduction automatique de la boutique.
  const syncBogo = async () => {
    if (window.__puffBogoBusy) return false;
    window.__puffBogoBusy = true;
    try {
      let cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((response) => response.json());
      let changed = false;
      const paidByVariant = new Map();
      const giftByVariant = new Map();
      cart.items.forEach((item) => {
        const variantId = Number(item.variant_id);
        if (item.properties && item.properties._offert) {
          giftByVariant.set(variantId, item);
        } else {
          paidByVariant.set(variantId, (paidByVariant.get(variantId) || 0) + Number(item.quantity || 0));
        }
      });

      for (const [variantId, gift] of giftByVariant) {
        const wanted = Math.floor((paidByVariant.get(variantId) || 0) / 2);
        if (wanted !== Number(gift.quantity || 0)) {
          changed = true;
          await fetch(changeUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: gift.key, quantity: wanted })
          });
        }
      }

      for (const [variantId, paidQuantity] of paidByVariant) {
        const wanted = Math.floor(paidQuantity / 2);
        if (wanted > 0 && !giftByVariant.has(variantId)) {
          changed = true;
          await fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: wanted, properties: { _offert: 'true' } }] })
          });
        }
      }

      if (changed) {
        cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((response) => response.json());
      }

      const paidGiftLines = cart.items.filter((item) => item.properties && item.properties._offert && Number(item.final_line_price || 0) > 0);
      for (const item of paidGiftLines) {
        changed = true;
        await fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });
      }
      return changed;
    } catch (_) { return false; }
    finally { window.__puffBogoBusy = false; }
  };
  window.PuffBogoSync = syncBogo;

  const toast = (message, options = {}) => {
    const el = document.getElementById('ThemeToast');
    if (!el) return;
    el.textContent = message;
    if (options.actions) {
      const wrap = document.createElement('div');
      wrap.className = 'theme-toast__actions';
      options.actions.forEach((action) => {
        const link = document.createElement('a');
        link.href = action.url;
        link.textContent = action.label;
        link.className = action.className || '';
        wrap.appendChild(link);
      });
      el.appendChild(wrap);
    }
    el.classList.add('is-visible');
    window.clearTimeout(el._timer);
    el._timer = window.setTimeout(() => el.classList.remove('is-visible'), options.duration || 3200);
  };

  const setCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
    document.querySelectorAll('[data-cart-link]').forEach((el) => {
      el.setAttribute('aria-label', `Panier, ${count} article${count !== 1 ? 's' : ''}`);
    });
  };

  const refreshCartCount = async () => {
    try {
      const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((response) => response.json());
      setCartCount(cart.item_count || 0);
    } catch (_) { /* The standard cart page remains available if this request fails. */ }
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  }[character]));

  const setupCartDrawer = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const itemsEl = drawer.querySelector('[data-cart-drawer-items]');
    const emptyEl = drawer.querySelector('[data-cart-drawer-empty]');
    const offerEl = drawer.querySelector('[data-cart-drawer-bogo]');
    const upsellEl = drawer.querySelector('[data-cart-drawer-bogo-upsell]');
    const shippingEl = drawer.querySelector('[data-cart-drawer-shipping]');
    const messageEl = drawer.querySelector('[data-cart-drawer-message]');
    const totalEl = drawer.querySelector('[data-cart-drawer-total]');
    const countEl = drawer.querySelector('[data-cart-drawer-count]');
    const savingsEl = drawer.querySelector('[data-cart-drawer-savings]');
    const originalTotalEl = drawer.querySelector('[data-cart-drawer-original-total]');
    const discountEl = drawer.querySelector('[data-cart-drawer-discount]');
    const checkoutButton = drawer.querySelector('[data-cart-drawer-checkout]');

    const setMessage = (message = '') => {
      if (!messageEl) return;
      messageEl.textContent = message;
      messageEl.hidden = !message;
    };

    const render = (cart) => {
      const items = Array.isArray(cart.items) ? cart.items : [];
      const itemCount = Number(cart.item_count || 0);
      if (upsellEl) {
        const singlePaidItem = items.find((item) => !(item.properties && item.properties._offert));
        upsellEl.hidden = itemCount !== 1 || !singlePaidItem;
        upsellEl.dataset.variantId = singlePaidItem ? String(singlePaidItem.variant_id) : '';
      }
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price || 0);
      if (checkoutButton) checkoutButton.disabled = items.length === 0;
      if (emptyEl) emptyEl.hidden = items.length > 0;
      if (itemsEl) itemsEl.hidden = items.length === 0;

      const giftVariantIds = new Set(items.filter((item) => item.properties && item.properties._offert && Number(item.final_line_price || 0) === 0).map((item) => Number(item.variant_id)));
      const visualGiftByVariant = new Map();
      items.forEach((item) => {
        if (item.properties && item.properties._offert) return;
        const quantity = Number(item.quantity || 0);
        if (quantity >= 2 && !giftVariantIds.has(Number(item.variant_id))) visualGiftByVariant.set(Number(item.variant_id), {
          quantity: Math.floor(quantity / 2),
          value: Number(item.final_price || item.price || 0),
          item
        });
      });
      const giftValue = Array.from(visualGiftByVariant.values()).reduce((sum, gift) => sum + (gift.value * gift.quantity), 0);
      const visualGiftCount = Array.from(visualGiftByVariant.values()).reduce((sum, gift) => sum + gift.quantity, 0);
      if (countEl) countEl.textContent = `(${itemCount + visualGiftCount})`;
      const eligible = giftValue > 0 || giftVariantIds.size > 0;
      const actualSavings = Math.max(0, Number(cart.original_total_price || cart.total_price || 0) - Number(cart.total_price || 0)) + giftValue;
      if (offerEl) offerEl.hidden = !eligible;
      if (savingsEl) savingsEl.hidden = actualSavings <= 0;
      if (originalTotalEl) originalTotalEl.textContent = formatMoney(Number(cart.original_total_price || cart.total_price || 0) + giftValue);
      if (discountEl) discountEl.textContent = `−${formatMoney(actualSavings)}`;

      if (shippingEl) {
        shippingEl.hidden = false;
        shippingEl.classList.add('is-unlocked');
        shippingEl.innerHTML = `
          <div class="pr-cart-drawer__shipping-head">
            <strong>Livraison offerte</strong>
            <span>Activée</span>
          </div>
          <p>Votre commande bénéficie de la livraison offerte.</p>
          <div class="pr-cart-drawer__shipping-progress" role="progressbar" aria-label="Livraison offerte" aria-valuemin="0" aria-valuemax="100" aria-valuenow="100">
            <span style="width:100%"></span>
          </div>`;
      }

      if (!itemsEl) return;
      itemsEl.innerHTML = items.map((item) => {
        const properties = Object.entries(item.properties || {})
          .filter(([key, value]) => key !== '_offert' && value !== '')
          .map(([key, value]) => `<small>${escapeHtml(key)} : ${escapeHtml(value)}</small>`)
          .join('');
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? `<small>${escapeHtml(item.variant_title)}</small>`
          : '';
        const isGift = Boolean(item.properties && item.properties._offert && Number(item.final_line_price || 0) === 0);
        const bogoLine = isGift
          ? '<small class="pr-cart-drawer__bogo-line">Votre article offert</small>'
          : giftVariantIds.has(Number(item.variant_id)) || visualGiftByVariant.has(Number(item.variant_id))
            ? '<small class="pr-cart-drawer__bogo-line">Offre 2 + 1 activée</small>'
          : '';
        const image = item.image
          ? `<img class="pr-cart-drawer__item-image" src="${escapeHtml(item.image)}" alt="" loading="lazy">`
          : '<div class="pr-cart-drawer__item-image" aria-hidden="true"></div>';
        const linePrice = Number(item.final_line_price || 0);
        const quantityControl = isGift
          ? `<small class="pr-cart-drawer__gift-note">Quantité ${Number(item.quantity)} · ajoutée automatiquement</small>`
          : `<div class="pr-cart-drawer__quantity">
                <button type="button" data-pr-drawer-quantity data-line="${escapeHtml(item.key)}" data-next="${Math.max(0, Number(item.quantity) - 1)}" aria-label="Diminuer la quantité"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/></svg></button>
                <span>${Number(item.quantity)}</span>
                <button type="button" data-pr-drawer-quantity data-line="${escapeHtml(item.key)}" data-next="${Number(item.quantity) + 1}" aria-label="Augmenter la quantité"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button>
             </div>
             <button type="button" class="pr-cart-drawer__remove" data-pr-drawer-quantity data-line="${escapeHtml(item.key)}" data-next="0">Retirer</button>`;
        const paidLine = `
          <article class="pr-cart-drawer__item${isGift ? ' pr-cart-drawer__item--gift' : ''}">
            ${image}
            <div class="pr-cart-drawer__item-info">
              <strong>${escapeHtml(item.product_title || item.title)}</strong>
              ${variant}${properties}${bogoLine}
              ${quantityControl}
            </div>
            <strong class="pr-cart-drawer__item-price">${isGift ? 'Gratuit' : formatMoney(linePrice)}</strong>
          </article>`;
        if (isGift || !visualGiftByVariant.has(Number(item.variant_id)) || giftVariantIds.has(Number(item.variant_id))) return paidLine;
        const gift = visualGiftByVariant.get(Number(item.variant_id));
        return paidLine + `
          <article class="pr-cart-drawer__item pr-cart-drawer__item--gift" data-bogo-visual-gift>
            ${image}
            <div class="pr-cart-drawer__item-info">
              <strong>${escapeHtml(item.product_title || item.title)}</strong>
              ${variant}
              <small class="pr-cart-drawer__bogo-line">Votre 3e article offert · quantité ${gift.quantity}</small>
            </div>
            <strong class="pr-cart-drawer__item-price">Gratuit</strong>
          </article>`;
      }).join('');
    };

    const load = async () => {
      setMessage('');
      try {
        const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error();
        const cart = await response.json();
        render(cart);
        setCartCount(cart.item_count || 0);
      } catch (_) {
        setMessage('Impossible de charger le panier. Le panier complet reste disponible.');
      }
    };

    const open = async () => {
      drawer.hidden = false;
      document.body.classList.add('pr-cart-open');
      await syncBogo();
      await load();
    };

    const close = () => {
      drawer.hidden = true;
      document.body.classList.remove('pr-cart-open');
    };

    drawer.addEventListener('click', async (event) => {
      const addAgainButton = event.target.closest('[data-pr-drawer-add-again]');
      if (addAgainButton) {
        const variantId = Number(upsellEl?.dataset.variantId || 0);
        if (!variantId) return;
        addAgainButton.disabled = true;
        try {
          const response = await fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });
          if (!response.ok) throw new Error();
          await syncBogo();
          await load();
        } catch (_) {
          setMessage('Impossible d’ajouter cet article pour le moment.');
        } finally {
          addAgainButton.disabled = false;
        }
        return;
      }
      const quantityButton = event.target.closest('[data-pr-drawer-quantity]');
      if (!quantityButton) return;
      const id = quantityButton.dataset.line;
      const quantity = Number(quantityButton.dataset.next);
      if (!id || !Number.isFinite(quantity)) return;
      quantityButton.disabled = true;
      try {
        const response = await fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id, quantity })
        });
        if (!response.ok) throw new Error();
        await syncBogo();
        await load();
      } catch (_) {
        setMessage('Impossible de mettre à jour cet article.');
      } finally {
        quantityButton.disabled = false;
      }
    });

    drawer.querySelectorAll('[data-cart-drawer-close]').forEach((element) => element.addEventListener('click', close));
    drawer.querySelector('[data-cart-drawer-checkout-form]')?.addEventListener('submit', () => close());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !drawer.hidden) close();
    });
    document.querySelectorAll('[data-cart-link]').forEach((link) => link.addEventListener('click', (event) => {
      event.preventDefault();
      open();
    }));
    window.openPrincessRoomCart = open;
  };


  const setupQuantityControls = () => {
    document.querySelectorAll('[data-quantity-minus]').forEach((button) => button.addEventListener('click', () => {
      const input = button.parentElement.querySelector('[data-quantity-input]');
      input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }));
    document.querySelectorAll('[data-quantity-plus]').forEach((button) => button.addEventListener('click', () => {
      const input = button.parentElement.querySelector('[data-quantity-input]');
      input.value = Number(input.value || 1) + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }));
  };

  const setupCartQuantityControls = () => {
    document.querySelectorAll('[data-cart-quantity-minus], [data-cart-quantity-plus]').forEach((button) => {
      button.addEventListener('click', async () => {
        const input = button.parentElement.querySelector('[data-cart-quantity-input]');
        const line = input?.dataset.line;
        if (!input || !line) return;
        const next = button.hasAttribute('data-cart-quantity-minus') ? Math.max(0, Number(input.value) - 1) : Number(input.value) + 1;
        input.value = next;
        try {
          await fetch(changeUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line: Number(line), quantity: next }) });
          await syncBogo();
          window.location.reload();
        } catch (_) { toast('Impossible de mettre à jour le panier.'); }
      });
    });
  };

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency }).format(cents / 100);
  };

  const setupProductVariant = () => {
    const rootEl = document.querySelector('[data-product-root]');
    const dataEl = document.querySelector('[data-product-json]');
    const select = rootEl?.querySelector('[data-variant-select]');
    if (!rootEl || !dataEl || !select) return;
    let variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (_) { return; }
    select.addEventListener('change', () => {
      const variant = variants.find((item) => String(item.id) === select.value);
      if (!variant) return;
      const price = rootEl.querySelector('[data-product-price]');
      if (price) {
        price.innerHTML = `<div class="price${variant.compare_at_price > variant.price ? ' price--sale' : ''}"><span class="price__current">${formatMoney(variant.price)}</span>${variant.compare_at_price > variant.price ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : ''}</div>`;
      }
      const submit = rootEl.querySelector('[data-add-to-cart]');
      if (submit) {
        submit.disabled = !variant.available;
        const label = submit.querySelector('span');
        if (label) label.textContent = variant.available ? 'Ajouter au panier' : 'Épuisé';
      }
      if (variant.featured_media?.preview_image?.src) {
        const image = rootEl.querySelector('[data-main-product-image]');
        if (image) image.src = variant.featured_media.preview_image.src;
      }
    });
  };

  const setupPredictiveSearch = () => {
    const forms = document.querySelectorAll('[data-predictive-search-form]');
    if (!forms.length) return;
    let productsPromise = null;
    const loadProducts = () => {
      if (!productsPromise) {
        const root = window.Shopify?.routes?.root || '/';
        productsPromise = fetch(`${root}products.json?limit=250`, { headers: { Accept: 'application/json' } })
          .then((response) => {
            if (!response.ok) throw new Error('search');
            return response.json();
          })
          .then((data) => Array.isArray(data.products) ? data.products : [])
          .catch(() => []);
      }
      return productsPromise;
    };
    const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[character]));
    const money = (value) => {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return '';
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' }).format(amount);
    };

    forms.forEach((form) => {
      const input = form.querySelector('[data-predictive-search-input]');
      const results = form.querySelector('[data-predictive-search-results]');
      const status = form.querySelector('[data-predictive-search-status]');
      const list = form.querySelector('[data-predictive-search-list]');
      const allLink = form.querySelector('[data-predictive-search-all]');
      if (!input || !results || !status || !list) return;
      const initialHelp = status.textContent;
      let timer = null;

      const render = async () => {
        const query = normalize(input.value);
        results.hidden = false;
        list.innerHTML = '';
        if (query.length < 2) {
          status.textContent = initialHelp;
          if (allLink) allLink.href = allLink.getAttribute('href').split('?')[0];
          return;
        }
        status.textContent = 'Recherche en cours…';
        const products = await loadProducts();
        const matches = products
          .filter((product) => normalize(product.title).includes(query))
          .sort((a, b) => {
            const aTitle = normalize(a.title);
            const bTitle = normalize(b.title);
            return Number(bTitle.startsWith(query)) - Number(aTitle.startsWith(query)) || aTitle.localeCompare(bTitle);
          })
          .slice(0, 8);
        status.textContent = matches.length ? `${matches.length} produit${matches.length > 1 ? 's' : ''} trouvé${matches.length > 1 ? 's' : ''}` : 'Aucun produit ne correspond à cette recherche.';
        list.innerHTML = matches.map((product) => {
          const image = product.images?.[0]?.src || product.image?.src || '';
          const price = product.variants?.[0]?.price;
          return `<a class="predictive-search__item" href="${escapeHtml(product.url || `/products/${product.handle}`)}">${image ? `<img src="${escapeHtml(image)}" alt="" width="64" height="64" loading="lazy">` : '<span class="predictive-search__placeholder" aria-hidden="true"></span>'}<span><strong>${escapeHtml(product.title)}</strong><small>${escapeHtml(money(price))}</small></span><b aria-hidden="true">→</b></a>`;
        }).join('');
        if (allLink) allLink.href = `${allLink.getAttribute('href').split('?')[0]}?q=${encodeURIComponent(input.value.trim())}&type=product`;
      };

      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(render, 120);
      });
      input.addEventListener('focus', render);
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') results.hidden = true;
      });
      form.addEventListener('submit', () => { results.hidden = true; });
    });
  };

  const closeFilters = () => {
    document.querySelector('[data-filter-panel]')?.classList.remove('is-open');
    document.body.classList.remove('filters-open');
  };

  document.addEventListener('DOMContentLoaded', () => {
    const header = document.querySelector('[data-site-header]');
    const updateHeader = () => header?.classList.toggle('is-scrolled', window.scrollY > 20);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });

    const searchDrawer = document.querySelector('[data-search-drawer]');
    document.querySelectorAll('[data-search-toggle]').forEach((button) => button.addEventListener('click', () => {
      const willOpen = searchDrawer.hidden;
      searchDrawer.hidden = !willOpen;
      button.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) searchDrawer.querySelector('[data-search-input]')?.focus();
    }));
    document.querySelectorAll('[data-search-close]').forEach((button) => button.addEventListener('click', () => {
      searchDrawer.hidden = true;
      document.querySelector('[data-search-toggle]')?.setAttribute('aria-expanded', 'false');
    }));
    setupPredictiveSearch();

    const mobileMenu = document.querySelector('[data-mobile-nav]');
    document.querySelectorAll('[data-menu-toggle]').forEach((button) => button.addEventListener('click', () => {
      mobileMenu.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      document.body.classList.add('menu-open');
    }));
    document.querySelectorAll('[data-menu-close]').forEach((button) => button.addEventListener('click', () => {
      mobileMenu.hidden = true;
      document.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
    }));

    document.querySelectorAll('[data-filter-toggle]').forEach((button) => button.addEventListener('click', () => {
      document.querySelector('[data-filter-panel]')?.classList.add('is-open');
      document.body.classList.add('filters-open');
    }));
    document.querySelectorAll('[data-filter-close]').forEach((button) => button.addEventListener('click', closeFilters));
    document.querySelectorAll('[data-clear-filters]').forEach((button) => button.addEventListener('click', () => { window.location.assign(button.closest('form').action); }));
    document.querySelectorAll('[data-sort-select]').forEach((select) => select.addEventListener('change', () => document.getElementById(select.getAttribute('form'))?.submit()));

    document.querySelectorAll('[data-expandable-toggle]').forEach((button) => button.addEventListener('click', () => {
      const content = button.parentElement.querySelector('[data-expandable-content]');
      const expanded = content.classList.toggle('is-expanded');
      button.querySelector('span').textContent = expanded ? 'Lire moins' : 'Lire plus';
      button.querySelector('.icon')?.classList.toggle('is-rotated', expanded);
    }));

    document.querySelectorAll('[data-product-form]').forEach((form) => form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submit = form.querySelector('[type="submit"]');
      if (submit) submit.disabled = true;
      try {
        const response = await fetch(addUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!response.ok) throw new Error();
        const bogoApplied = await syncBogo();
        await refreshCartCount();
        toast(bogoApplied ? 'Produit ajouté — offre 2 achetés = 1 offert activée dans le panier.' : 'Produit ajouté au panier.', {
          duration: 6500,
          actions: [
            { label: 'Voir le panier', url: `${root}cart`, className: 'theme-toast__btn--cart' }
          ]
        });
        window.openPrincessRoomCart?.();
      } catch (_) { toast('Impossible d’ajouter ce produit au panier.'); }
      finally { if (submit) submit.disabled = false; }
    }));

    document.querySelectorAll('[data-media-thumb]').forEach((thumb) => thumb.addEventListener('click', () => {
      const gallery = thumb.closest('[data-product-gallery]');
      const image = gallery?.querySelector('[data-main-product-image]');
      if (image) { image.src = thumb.dataset.mediaUrl; image.alt = thumb.dataset.mediaAlt || ''; }
      gallery?.querySelectorAll('[data-media-thumb]').forEach((el) => el.classList.remove('is-active'));
      thumb.classList.add('is-active');
    }));

    setupQuantityControls();
    setupCartQuantityControls();
    setupProductVariant();
    setupCartDrawer();

    document.querySelector('[data-recover-toggle]')?.addEventListener('click', (event) => {
      event.preventDefault();
      document.querySelector('.recover-form')?.removeAttribute('hidden');
    });
    document.querySelector('[data-address-new]')?.addEventListener('click', () => document.querySelector('[data-address-new-form]')?.removeAttribute('hidden'));
    document.querySelectorAll('[data-address-edit]').forEach((button) => button.addEventListener('click', () => document.getElementById(`EditAddress_${button.dataset.addressEdit}`)?.removeAttribute('hidden')));
    document.querySelectorAll('[data-address-cancel]').forEach((button) => button.addEventListener('click', () => button.closest('.address-form-wrap')?.setAttribute('hidden', '')));
  });
})();
